/** Popup watermark — display: src/config/watermark.config.js; visibility: API `watermark`. */

import { WATERMARK_CONFIG } from "../config/watermark.config";
import { getStepSectionsForPopup } from "./popup-step-sections";

const VALID_POSITIONS = new Set([
  "top-left",
  "top-center",
  "top-right",
  "center-left",
  "center",
  "center-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
]);

/** Steps that only open the popup (tab/trigger) — no "Powered by" badge on these. */
const TRIGGER_ONLY_SECTION_TYPES = new Set([
  "BUTTON",
  "BUTTON1",
  "SHOP_BUTTON",
  "TRIGGER",
]);

function normalizeFromConfig() {
  const raw = WATERMARK_CONFIG || {};
  const gap = Number(raw.offsetGap);
  return {
    text:
      typeof raw.text === "string" && raw.text.trim()
        ? raw.text.trim()
        : "Powered by WeUpsell",
    logoUrl: typeof raw.logoUrl === "string" ? raw.logoUrl.trim() : "",
    position: VALID_POSITIONS.has(raw.position) ? raw.position : "bottom-right",
    offsetGap:
      Number.isFinite(gap) && gap >= 0
        ? Math.min(32, Math.max(4, Math.round(gap)))
        : 12,
    liveUrl:
      typeof raw.liveUrl === "string" && raw.liveUrl.trim()
        ? raw.liveUrl.trim()
        : "https://weupsell.com",
    editorContact: raw.editorContact || {},
  };
}

/** Resolved badge display settings (internal config only). */
export function resolveWatermarkDisplay() {
  return normalizeFromConfig();
}

/** @deprecated Use resolveWatermarkDisplay */
export const resolveAppWatermark = resolveWatermarkDisplay;

export function buildWhatsAppSupportUrl(contact, shopHint = "") {
  const id = String(contact?.whatsappWaMeId || "").replace(/\D/g, "");
  if (!id) return null;
  const message = [contact?.whatsappMessage, shopHint].filter(Boolean).join("\n\n");
  const q = message ? `?text=${encodeURIComponent(message)}` : "";
  return `https://wa.me/${id}${q}`;
}

export function buildEmailSupportUrl(contact, shopHint = "") {
  const email = contact?.email;
  if (!email) return null;
  const body = [contact?.emailBody, shopHint].filter(Boolean).join("\n\n");
  const params = new URLSearchParams();
  if (contact?.emailSubject) params.set("subject", contact.emailSubject);
  if (body) params.set("body", body);
  const qs = params.toString();
  return `mailto:${email}${qs ? `?${qs}` : ""}`;
}

export function getEditorWatermarkHref(shopHint = "") {
  const { editorContact } = normalizeFromConfig();
  const prefer = editorContact?.preferred === "email" ? "email" : "whatsapp";
  if (prefer === "whatsapp") {
    const wa = buildWhatsAppSupportUrl(editorContact, shopHint);
    if (wa) return wa;
  }
  return buildEmailSupportUrl(editorContact, shopHint) || normalizeFromConfig().liveUrl;
}

export function getLiveWatermarkHref() {
  return normalizeFromConfig().liveUrl;
}

/**
 * Show/hide from shop Mongo field or GET /shop / app-branding `watermark` object.
 * Popup API does not include this — only `shops.hideWatermark`.
 */
export function resolveWatermarkVisibility(watermark, shop) {
  const shopDoc = shop && typeof shop === "object" ? shop : null;
  const watermarkDoc = watermark && typeof watermark === "object" ? watermark : null;

  if (shopDoc?.hideWatermark === true || shopDoc?.hideAppBranding === true) {
    return { enabled: false, hideWatermark: true };
  }

  if (shopDoc?.hideWatermark === false) {
    return { enabled: true, hideWatermark: false };
  }

  if (watermarkDoc?.hideWatermark === true) {
    return { enabled: false, hideWatermark: true };
  }

  if (watermarkDoc && typeof watermarkDoc.enabled === "boolean") {
    return watermarkDoc;
  }

  return { enabled: true };
}

/** @deprecated Use resolveWatermarkVisibility */
export function isWatermarkEnabled(watermark, shop) {
  return resolveWatermarkVisibility(watermark, shop).enabled;
}

function isButtonTriggerSection(section) {
  return (
    !!section?.type && TRIGGER_ONLY_SECTION_TYPES.has(String(section.type))
  );
}

/**
 * Hide watermark when every section on the step is a button/trigger (1 or many).
 * Show watermark when any other section type is present (e.g. slider + button).
 */
export function isTriggerOnlyStep(popupData, activeStep) {
  const sections = getStepSectionsForPopup(popupData, activeStep);
  const typed = sections.filter((s) => s?.type);
  if (!typed.length) return false;
  return typed.every(isButtonTriggerSection);
}

export function shouldShowPopupWatermark(
  popupData,
  activeStep,
  watermark,
  _isPreviewMode = false,
  shop
) {
  if (!isWatermarkEnabled(watermark, shop)) return false;
  if (isTriggerOnlyStep(popupData, activeStep)) return false;

  const sections = getStepSectionsForPopup(popupData, activeStep);
  return sections.length > 0;
}

/** In-flow badge below upsell blocks — avoids overlapping theme cart lines. */
export function getCartDrawerWatermarkPositionStyle() {
  return getCartDrawerBrandAttributionPositionStyle();
}

/** Centered AI attribution line below cart drawer upsell actions. */
export function getCartDrawerBrandAttributionPositionStyle() {
  return {
    position: "relative",
    zIndex: 1,
    margin: 0,
    marginTop: "8px",
    paddingTop: "2px",
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    width: "100%",
    boxSizing: "border-box",
  };
}

export const CART_DRAWER_WATERMARK_COMPACT = {
  padding: "3px 6px",
  fontSize: "9px",
  gap: "4px",
  logoSize: 12,
  borderRadius: "6px",
};

export function getOutsideWatermarkPositionStyle(position, gapPx = 12) {
  const gap = Number.isFinite(gapPx) ? gapPx : 12;
  const base = {
    position: "absolute",
    zIndex: 15,
    margin: 0,
    pointerEvents: "none",
  };

  switch (position) {
    case "top-left":
      return { ...base, left: 0, bottom: `calc(100% + ${gap}px)` };
    case "top-center":
      return {
        ...base,
        left: "50%",
        bottom: `calc(100% + ${gap}px)`,
        transform: "translateX(-50%)",
      };
    case "top-right":
      return { ...base, right: 0, bottom: `calc(100% + ${gap}px)` };
    case "center-left":
      return {
        ...base,
        right: `calc(100% + ${gap}px)`,
        top: "50%",
        transform: "translateY(-50%)",
      };
    case "center":
    case "center-right":
      return {
        ...base,
        left: `calc(100% + ${gap}px)`,
        top: "50%",
        transform: "translateY(-50%)",
      };
    case "bottom-left":
      return { ...base, left: 0, top: `calc(100% + ${gap}px)` };
    case "bottom-center":
      return {
        ...base,
        left: "50%",
        top: `calc(100% + ${gap}px)`,
        transform: "translateX(-50%)",
      };
    case "bottom-right":
    default:
      return { ...base, right: 0, top: `calc(100% + ${gap}px)` };
  }
}
